import { useMemo, useState } from "react";

/**
 * Approved Workforce hero widget - structure from workforce-mini.js renderOverviewMini
 * + Overview/Leave/Reporting/Compliance tabs. Dark marketing theme.
 * Product refs: marketing/public/workforce-mini.js, home/Workforce.jsx
 */

const UNITS = ["Cape Town", "Johannesburg", "Durban", "Pretoria", "Gqeberha", "Remote"];
const GENDERS = ["Female", "Male"];
const RACES = ["African", "Coloured", "Indian", "White", "Other"];
const AGE_DOTS = {
  "Under 25": "bg-orange-400",
  "25-34": "bg-pink-400",
  "35-44": "bg-violet-400",
  "45-54": "bg-teal",
  "55+": "bg-sky-400",
};
const RACE_DOTS = {
  African: "bg-teal",
  Indian: "bg-pink-400",
  White: "bg-violet-400",
  Coloured: "bg-sky-400",
  Other: "bg-orange-400",
};

const rnd = (seed) => {
  const x = Math.sin(seed * 999.7) * 10000;
  return x - Math.floor(x);
};

const pickWeighted = (seed, items, weights) => {
  let t = rnd(seed);
  for (let i = 0; i < items.length; i += 1) {
    t -= weights[i];
    if (t <= 0) return items[i];
  }
  return items[items.length - 1];
};

const ageGroup = (age) => {
  if (age < 25) return "Under 25";
  if (age < 35) return "25-34";
  if (age < 45) return "35-44";
  if (age < 55) return "45-54";
  return "55+";
};

const iso = (y, m, d) => `${y}-${String(m).padStart(2, "0")}-${String(Math.min(28, d)).padStart(2, "0")}`;

function buildPeople() {
  const FIRST = ["Amina", "Lerato", "James", "Priya", "Thabo", "Sarah", "Kwame", "Elena", "Noah", "Fatima", "Pieter", "Maya"];
  const LAST = ["Nkosi", "Patel", "Botha", "Chen", "Meyer", "Naidoo", "Williams", "Dlamini", "Khan", "Jacobs", "Ncube", "Reed"];
  const ACTIVE = 612;
  const JOINERS = 50;
  const EXITS = 23;
  const people = [];
  for (let i = 0; i < ACTIVE + EXITS; i += 1) {
    const terminated = i >= ACTIVE;
    const isNew = !terminated && i < JOINERS;
    const age = 22 + Math.floor(rnd(i + 18) * 38);
    const los = terminated ? 0.4 + rnd(i + 41) * 4 : isNew ? 0.2 + rnd(i + 42) * 0.6 : 1.1 + rnd(i + 43) * 6;
    const empYear = isNew ? 2026 : 2018 + Math.floor(rnd(i + 8) * 7);
    const empMonth = isNew ? 2 + Math.floor(rnd(i + 11) * 6) : 1 + Math.floor(rnd(i + 12) * 12);
    people.push({
      id: 10001 + i,
      name: `${FIRST[i % FIRST.length]} ${LAST[Math.floor(rnd(i + 3) * LAST.length)]}`,
      gender: pickWeighted(i + 12, GENDERS, [0.54, 0.46]),
      race: pickWeighted(i + 27, RACES, [0.42, 0.12, 0.18, 0.16, 0.12]),
      age,
      age_group: ageGroup(age),
      org_unit: UNITS[Math.floor(rnd(i + 9) * UNITS.length)],
      employment_date: iso(empYear, Math.min(12, empMonth), 1 + Math.floor(rnd(i + 14) * 27)),
      termination_date: terminated
        ? iso(2026, 3 + Math.floor(rnd(i + 70) * 5), 1 + Math.floor(rnd(i + 71) * 26))
        : "",
      active: !terminated,
      new: isNew,
      terminated,
      los_years: Math.round(los * 10) / 10,
    });
  }
  return people;
}

function countRows(rows, key) {
  const map = new Map();
  for (const row of rows) {
    const label = String(row?.[key] || "Unknown");
    map.set(label, (map.get(label) || 0) + 1);
  }
  const total = [...map.values()].reduce((a, b) => a + b, 0) || 1;
  return [...map.entries()]
    .map(([label, count]) => ({
      label,
      count,
      percent: Math.round((1000 * count) / total) / 10,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

function filterPeople(list, filters) {
  return list.filter((p) => {
    if (filters.gender && p.gender !== filters.gender) return false;
    if (filters.race && p.race !== filters.race) return false;
    if (filters.age_group && p.age_group !== filters.age_group) return false;
    if (filters.org_unit && p.org_unit !== filters.org_unit) return false;
    return true;
  });
}

function buildHeadcountByMonth(list) {
  const months = [];
  for (let m = 3; m <= 8; m += 1) {
    const monthStart = new Date(2026, m - 1, 1);
    const monthEnd = new Date(2026, m, 0);
    let active = 0;
    for (const person of list) {
      const empStart = person.employment_date ? new Date(`${person.employment_date}T00:00:00`) : null;
      const empEnd = person.termination_date ? new Date(`${person.termination_date}T00:00:00`) : null;
      if ((!empStart || empStart <= monthEnd) && (!empEnd || empEnd >= monthStart)) active += 1;
    }
    months.push({ label: monthStart.toLocaleString("en-ZA", { month: "short" }), active });
  }
  return months;
}

function summarizePeople(list) {
  const active = list.filter((p) => p.active);
  const joined = list.filter((p) => p.new);
  const terminated = list.filter((p) => p.terminated);
  const ages = active.map((p) => p.age);
  const los = active.map((p) => p.los_years);
  const gender = countRows(active, "gender");
  return {
    headcount: { active: active.length, new: joined.length, terminated: terminated.length },
    averages: {
      age: ages.length ? Math.round((ages.reduce((a, b) => a + b, 0) / ages.length) * 10) / 10 : 0,
      los: los.length ? Math.round((los.reduce((a, b) => a + b, 0) / los.length) * 10) / 10 : 0,
    },
    gender,
    race: countRows(active, "race"),
    age_groups: countRows(active, "age_group"),
    org_units: countRows(active, "org_unit"),
    headcount_by_month: buildHeadcountByMonth(list),
  };
}

const PEOPLE = buildPeople();
const VIEWS = [
  { id: "overview", label: "Overview" },
  { id: "leave", label: "Leave" },
  { id: "reporting", label: "Reporting" },
  { id: "compliance", label: "Compliance" },
];

function toggleFilter(filters, key, value) {
  const next = { ...filters };
  if (next[key] === value) delete next[key];
  else next[key] = value;
  return next;
}

export default function HeroWorkforce() {
  const [view, setView] = useState("overview");
  const [filters, setFilters] = useState({});

  const summary = useMemo(() => summarizePeople(filterPeople(PEOPLE, filters)), [filters]);
  const female = summary.gender.find((r) => r.label === "Female");
  const male = summary.gender.find((r) => r.label === "Male");
  const monthVals = summary.headcount_by_month.map((m) => m.active);
  const lo = Math.min(...monthVals);
  const hi = Math.max(...monthVals);
  const span = Math.max(12, hi - lo);
  const filterEntries = Object.entries(filters).filter(([, v]) => v);
  const setFilter = (key, value) => setFilters((f) => toggleFilter(f, key, value));

  return (
    <div className="flex flex-col gap-1.5" data-testid="hero-ws-workforce">
      <div className="min-w-0 shrink-0">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-teal">Workforce</p>
        <p className="text-[14px] font-medium leading-snug text-slate-100 sm:text-[15px]">
          People movement and composition
        </p>
        <p className="truncate text-[11px] text-slate-500">
          Live company view for Northgate Retail · {summary.headcount.active} active
        </p>
      </div>

      <div className="flex shrink-0 flex-wrap gap-1" role="tablist" aria-label="Workforce views">
        {VIEWS.map((t) => {
          const active = view === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`hero-ws-wf-${t.id}`}
              onClick={() => setView(t.id)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal/50 sm:text-[12px] ${
                active
                  ? "bg-teal text-[#05201b]"
                  : "border border-white/10 bg-white/[0.03] text-slate-400 hover:text-slate-200"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {view === "overview" ? (
        <div className="flex flex-col gap-1.5">
          {filterEntries.length ? (
            <div className="flex shrink-0 flex-wrap items-center gap-1">
              {filterEntries.map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFilter(k, v)}
                  className="rounded-full border border-teal/35 bg-teal/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] text-teal"
                >
                  {k.replace(/_/g, " ")}: {v} ×
                </button>
              ))}
              <button
                type="button"
                onClick={() => setFilters({})}
                className="rounded-full border border-white/15 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] text-slate-400"
              >
                Clear
              </button>
            </div>
          ) : null}

          <div className="grid shrink-0 grid-cols-5 gap-1">
            {[
              { k: "Active", v: String(summary.headcount.active) },
              { k: "Joiners", v: `+${summary.headcount.new}`, valueClass: "text-teal" },
              {
                k: "Exits",
                v: `−${summary.headcount.terminated}`,
                accent: true,
                valueClass: "text-amber-200",
              },
              { k: "Avg age", v: String(summary.averages.age) },
              { k: "LoS years", v: String(summary.averages.los) },
            ].map((m) => (
              <div
                key={m.k}
                className={`rounded-lg px-1 py-1.5 text-center sm:px-1.5 ${
                  m.accent
                    ? "border border-amber-300/35 bg-amber-300/[0.06]"
                    : "border border-white/[0.07] bg-white/[0.025]"
                }`}
              >
                <p
                  className={`font-display text-[15px] font-semibold tracking-tight sm:text-[17px] ${
                    m.valueClass || "text-slate-50"
                  }`}
                >
                  {m.v}
                </p>
                <p className="text-[10px] text-slate-500 sm:text-[11px]">{m.k}</p>
              </div>
            ))}
          </div>

          <div className="grid shrink-0 grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={() => female && setFilter("gender", "Female")}
              aria-pressed={filters.gender === "Female"}
              className={`rounded-lg border px-2 py-1.5 text-left transition-colors ${
                filters.gender === "Female"
                  ? "border-teal/45 bg-teal/[0.12]"
                  : "border-white/[0.07] bg-white/[0.025] hover:border-white/15"
              }`}
            >
              <p className="font-display text-base font-semibold text-slate-50 sm:text-lg">
                {female ? `${female.percent}%` : "-"}
              </p>
              <p className="text-[11px] text-slate-400 sm:text-[12px]">
                Female · {female ? female.count : 0}
              </p>
            </button>
            <button
              type="button"
              onClick={() => male && setFilter("gender", "Male")}
              aria-pressed={filters.gender === "Male"}
              className={`rounded-lg border px-2 py-1.5 text-left transition-colors ${
                filters.gender === "Male"
                  ? "border-teal/45 bg-teal/[0.12]"
                  : "border-white/[0.07] bg-white/[0.025] hover:border-white/15"
              }`}
            >
              <p className="font-display text-base font-semibold text-slate-50 sm:text-lg">
                {male ? `${male.percent}%` : "-"}
              </p>
              <p className="text-[11px] text-slate-400 sm:text-[12px]">Male · {male ? male.count : 0}</p>
            </button>
          </div>

          <div className="grid shrink-0 grid-cols-2 gap-1.5">
            <div className="rounded-lg border border-white/[0.07] bg-white/[0.025] px-1.5 py-1">
              <ul className="space-y-0">
                {summary.age_groups.slice(0, 4).map((row) => {
                  const on = filters.age_group === row.label;
                  return (
                    <li key={row.label}>
                      <button
                        type="button"
                        onClick={() => setFilter("age_group", row.label)}
                        aria-pressed={on}
                        className={`flex w-full items-center justify-between gap-2 rounded-md px-1 py-0.5 text-[11px] transition-colors sm:text-[12px] ${
                          on ? "bg-teal/15 text-teal" : "text-slate-400 hover:bg-white/[0.04] hover:text-slate-200"
                        }`}
                      >
                        <span className="flex items-center gap-1.5">
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${AGE_DOTS[row.label] || "bg-slate-500"}`}
                            aria-hidden="true"
                          />
                          {row.label}
                        </span>
                        <span className="font-mono text-slate-300">{row.count}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="rounded-lg border border-white/[0.07] bg-white/[0.025] px-1.5 py-1">
              <ul className="space-y-0">
                {summary.race.slice(0, 4).map((row) => {
                  const on = filters.race === row.label;
                  return (
                    <li key={row.label}>
                      <button
                        type="button"
                        onClick={() => setFilter("race", row.label)}
                        aria-pressed={on}
                        className={`flex w-full items-center justify-between gap-2 rounded-md px-1 py-0.5 text-[11px] transition-colors sm:text-[12px] ${
                          on ? "bg-teal/15 text-teal" : "text-slate-400 hover:bg-white/[0.04] hover:text-slate-200"
                        }`}
                      >
                        <span className="flex items-center gap-1.5">
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${RACE_DOTS[row.label] || "bg-slate-500"}`}
                            aria-hidden="true"
                          />
                          {row.label}
                        </span>
                        <span className="font-mono text-slate-300">{row.count}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="shrink-0 rounded-lg border border-white/[0.07] bg-white/[0.02] px-2.5 py-1.5">
            <div className="flex h-[28px] items-end gap-1.5 sm:h-[32px] sm:gap-2">
              {summary.headcount_by_month.map((m) => {
                const h = 10 + Math.round((20 * (m.active - lo)) / span);
                return (
                  <div key={m.label} className="flex h-full flex-1 flex-col justify-end">
                    <div
                      className="w-full rounded-sm bg-gradient-to-t from-cy/80 to-teal"
                      style={{ height: `${h}px` }}
                      title={`${m.label}: ${m.active}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="mt-0.5 flex justify-between font-mono text-[9px] uppercase tracking-[0.1em] text-slate-600">
              {summary.headcount_by_month.map((m) => (
                <span key={m.label}>{m.label}</span>
              ))}
            </div>
          </div>

          <div className="grid shrink-0 grid-cols-3 gap-1">
            {summary.org_units.slice(0, 3).map((row) => {
              const on = filters.org_unit === row.label;
              return (
                <button
                  key={row.label}
                  type="button"
                  onClick={() => setFilter("org_unit", row.label)}
                  aria-pressed={on}
                  className={`flex items-center justify-between rounded-md border px-2 py-1 text-left ${
                    on
                      ? "border-teal/45 bg-teal/[0.12]"
                      : "border-white/[0.07] bg-white/[0.025] hover:border-white/15"
                  }`}
                >
                  <span className="truncate text-[11px] text-slate-400 sm:text-[12px]">{row.label}</span>
                  <span className="font-mono text-[11px] text-teal sm:text-[12px]">{row.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {view === "leave" ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden" data-testid="hero-ws-wf-leave-panel">
          <div className="grid grid-cols-4 gap-1.5">
            {[
              ["On leave", "6"],
              ["Rejected", "30"],
              ["In workflow", "7"],
              ["Liability", "R 20.9m"],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-white/[0.07] bg-white/[0.025] px-1.5 py-2.5 text-center">
                <p className="font-display text-[15px] font-semibold text-slate-50 sm:text-lg">{v}</p>
                <p className="mt-0.5 text-[11px] text-slate-500">{k}</p>
              </div>
            ))}
          </div>
          <div className="grid min-h-0 flex-1 grid-cols-2 gap-1.5 overflow-hidden">
            <div className="rounded-lg border border-white/[0.07] bg-white/[0.02] px-2.5 py-2">
              <p className="text-[11px] text-slate-500">Leave days taken</p>
              {[
                ["Annual", "940.8"],
                ["Sick", "142.3"],
                ["Family", "28"],
              ].map(([n, d]) => (
                <div key={n} className="mt-1.5 flex items-center justify-between text-[13px]">
                  <span className="text-slate-300">{n}</span>
                  <span className="font-mono text-teal">{d}</span>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-white/[0.07] bg-white/[0.02] px-2.5 py-2">
              <p className="text-[11px] text-slate-500">Top balances</p>
              {[
                ["K. Govender", "14.3d"],
                ["T. Dale", "11.3d"],
                ["A. Mason", "11.3d"],
              ].map(([n, d]) => (
                <div key={n} className="mt-1.5 flex items-center justify-between text-[13px]">
                  <span className="truncate text-slate-300">{n}</span>
                  <span className="font-mono text-teal">{d}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {view === "reporting" ? (
        <div className="flex min-h-0 flex-1 flex-col justify-center gap-2 overflow-hidden" data-testid="hero-ws-wf-reporting-panel">
          <div className="rounded-lg border border-white/[0.07] bg-white/[0.02] px-3 py-3">
            <p className="text-[14px] font-medium text-slate-100">Coming next</p>
            <p className="mt-1 text-[13px] text-slate-500">
              Custom breakdowns and export-ready reporting - same board shell as the app.
            </p>
          </div>
          {[
            ["Custom slices", "Criteria you define"],
            ["Comparisons", "Period and org views"],
            ["Exports", "Excel or PDF ready"],
          ].map(([name, meta]) => (
            <div key={name} className="rounded-lg border border-white/[0.07] bg-white/[0.02] px-3 py-2.5">
              <p className="text-[13px] text-slate-200">{name}</p>
              <p className="mt-0.5 text-[12px] text-slate-500">{meta}</p>
            </div>
          ))}
        </div>
      ) : null}

      {view === "compliance" ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden" data-testid="hero-ws-wf-compliance-panel">
          <div className="grid grid-cols-4 gap-1.5">
            {[
              ["Items", "4"],
              ["Hours", "2"],
              ["Wage floor", "1"],
              ["Missing refs", "1"],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-white/[0.07] bg-white/[0.025] px-1.5 py-2.5 text-center">
                <p className="font-display text-lg font-semibold text-slate-50">{v}</p>
                <p className="mt-0.5 text-[11px] text-slate-500">{k}</p>
              </div>
            ))}
          </div>
          {[
            ["Amina Nkosi", "Hours"],
            ["James Patel", "Wage floor"],
            ["Priya Botha", "Missing ref"],
            ["Thabo Chen", "Hours"],
          ].map(([name, kind]) => (
            <div
              key={name}
              className="flex items-center justify-between rounded-lg border border-amber-300/25 bg-amber-300/[0.05] px-3 py-2.5"
            >
              <span className="truncate text-[13px] text-slate-200">{name}</span>
              <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-amber-200">{kind}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
